import { useRef, useState } from "react"

import { useParams } from "react-router-dom"

import { useMutation, useQuery } from "@tanstack/react-query"

import { Mail, KeyRound, ShieldCheck, ArrowLeft, CheckCircle2, Clock, Ban } from "lucide-react"

import consentResignStatusMutation from "./services/consent-resign-status"
import requestCodeMutation from "./services/request-consent-resign-code"
import verifyCodeMutation from "./services/verify-consent-resign-code"
import submitConsentResignMutation from "./services/submit-consent-resign"

import PublicHeader from "../../publicHeader"
import BackgroundCheckStepper from "../BackgroundCheckForm/Components/BackgroundCheckStepper"
import LegalDisclosuresAndConsentHub from "../PreCheckForm/Components/LegalDisclosuresAndConsentHub"
import WelcomeAndStatusSelection from "../PreCheckForm/Components/WelcomeAndStatusSelection"
import SignatureAndIdentifier from "../PreCheckForm/Components/SignatureAndIdentifier"

const STEP = {
    EMAIL: "email",
    CODE: "code",
    CONSENT: "consent",
    DONE: "done",
}

// The consent pages in the same order as the candidate form. Only the last one is editable.
const CONSENT_PAGES = [
    { type: "legal_disclosures_and_consent_hub", title: "Legal Disclosures & Consent" },
    { type: "welcome_and_status_selection", title: "Welcome & Status" },
    { type: "signature_and_identifier", title: "Signature & Identifier" },
]

const apiError = (err, fallback) => err?.response?.data?.error || fallback

const formatDate = (date) => (date ? new Date(date).toLocaleDateString("en-US") : null)

// Shown instead of the flow when the link cannot be used (already signed / window closed).
const StateCard = ({ status, signedAt }) => {
    const copy = {
        signed: {
            Icon: CheckCircle2,
            iconClass: "text-green-600 bg-green-50 border-green-200",
            title: "Consent already signed",
            body: `Your consent was signed${signedAt ? ` on ${formatDate(signedAt)}` : ""}. No further action is needed.`,
        },
        expired: {
            Icon: Clock,
            iconClass: "text-amber-600 bg-amber-50 border-amber-200",
            title: "This request has expired",
            body: "The time to re-sign your consent has passed. If you have questions, please contact the TruCheck™ QA Compliance Team at trucheck.qareview@verifiedid.co.",
        },
        closed: {
            Icon: Ban,
            iconClass: "text-gray-600 bg-gray-50 border-gray-200",
            title: "This request is no longer available",
            body: "This consent request has been closed or replaced by a newer request. Please use the most recent email you received, or contact trucheck.qareview@verifiedid.co.",
        },
    }[status] || {
        Icon: Ban,
        iconClass: "text-gray-600 bg-gray-50 border-gray-200",
        title: "Link not found",
        body: "This link is invalid. Please use the link from your most recent email.",
    }
    const { Icon } = copy
    return (
        <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg p-6 text-center">
            <div className={`w-12 h-12 mx-auto rounded-full border flex items-center justify-center mb-4 ${copy.iconClass}`}>
                <Icon className="w-6 h-6" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{copy.title}</h2>
            <p className="text-sm text-gray-600 mt-2">{copy.body}</p>
        </div>
    )
}

// Pre-adverse CONSENT RE-SIGN page (/consent-resign/:resignKey). QA asked the candidate to sign their
// TruCheck consent again (e.g. the last 4 SSN on the signature page was wrong). The candidate confirms
// their email + a one-time code, then walks the consent pages: the disclosures and status selection
// are shown read-only exactly as first agreed, and only the signature page is filled and submitted.
const ConsentResign = () => {
    const { resignKey } = useParams()

    const [step, setStep] = useState(STEP.EMAIL)
    const [email, setEmail] = useState("")
    const [code, setCode] = useState("")
    const [pageIndex, setPageIndex] = useState(0)
    const [context, setContext] = useState(null)
    const [submitError, setSubmitError] = useState("")

    const signatureRef = useRef()

    const { data: linkState, isLoading: isLoadingState, error: linkStateError } = useQuery({
        queryKey: ["consent-resign-status", resignKey],
        queryFn: () => consentResignStatusMutation({ resignKey }),
        enabled: !!resignKey,
        retry: false,
        refetchOnWindowFocus: false,
    })

    const { mutate: requestCode, isPending: isRequestingCode, error: requestCodeError } = useMutation({
        mutationFn: requestCodeMutation,
        onSuccess: () => setStep(STEP.CODE),
    })

    const { mutate: verifyCode, isPending: isVerifyingCode, error: verifyCodeError } = useMutation({
        mutationFn: verifyCodeMutation,
        onSuccess: (data) => {
            setContext(data)
            setPageIndex(0)
            setStep(STEP.CONSENT)
            window.scrollTo(0, 0)
        },
    })

    const { mutate: submitConsent, isPending: isSubmitting } = useMutation({
        mutationFn: submitConsentResignMutation,
        onSuccess: () => {
            setStep(STEP.DONE)
            window.scrollTo(0, 0)
        },
        onError: (err) => {
            // An expired session needs a fresh code; anything else stays on the signature page.
            if (err?.response?.status === 401) {
                setContext(null)
                setCode("")
                setStep(STEP.EMAIL)
            }
            setSubmitError(apiError(err, "Your consent could not be submitted. Please try again."))
        },
    })

    const onSubmitEmail = (e) => {
        e.preventDefault()
        if (!email.trim()) return
        setSubmitError("")
        requestCode({ resignKey, email: email.trim() })
    }

    const onSubmitCode = (e) => {
        e.preventDefault()
        if (!code.trim()) return
        verifyCode({ resignKey, code: code.trim() })
    }

    const goNext = () => {
        setPageIndex((index) => Math.min(index + 1, CONSENT_PAGES.length - 1))
        window.scrollTo(0, 0)
    }

    const onSign = () => {
        const result = signatureRef.current?.validateAndGetData()
        if (!result?.isValid) return
        setSubmitError("")
        submitConsent({
            resignKey,
            sessionToken: context.sessionToken,
            fullName: result.value.fullName,
            lastFourSSN: result.value.lastFourSSN,
            confirmLastFourSSN: result.value.lastFourSSN,
            agreeFinal: result.value.agreeFinal,
            date: result.value.date,
        })
    }

    const referenceNumber = context?.referenceNumber || linkState?.referenceNumber

    // STEP 3 — the consent pages.
    if (step === STEP.CONSENT && context) {
        const page = CONSENT_PAGES[pageIndex]
        const pageData = context.pages?.[page.type] || {}
        return (
            <div className="min-h-screen flex flex-col bg-gray-50">
                <PublicHeader referenceNumber={referenceNumber} />
                <BackgroundCheckStepper steps={CONSENT_PAGES.map((p) => p.title)} currentStep={pageIndex} />

                <div className="max-w-3xl w-full mx-auto px-4 pt-6">
                    <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                        <p className="font-semibold">Re-sign your TruCheck™ consent</p>
                        <p className="mt-1">
                            Your previous selections are shown as you first provided them and stay the same. Please
                            review them, then re-enter your full legal name and the last 4 digits of your SSN on the
                            signature page.
                        </p>
                        {context.comment && (
                            <p className="mt-2 text-blue-800 whitespace-pre-wrap"><span className="font-semibold">Note from the QA team:</span> {context.comment}</p>
                        )}
                    </div>
                </div>

                <div className="flex flex-col flex-grow items-center">
                    {page.type === "legal_disclosures_and_consent_hub" && (
                        <LegalDisclosuresAndConsentHub
                            key={page.type}
                            data={pageData}
                            company={context.company}
                            handleNext={goNext}
                            readOnly
                        />
                    )}
                    {page.type === "welcome_and_status_selection" && (
                        <WelcomeAndStatusSelection
                            key={page.type}
                            data={pageData}
                            handleNext={goNext}
                            readOnly
                        />
                    )}
                    {page.type === "signature_and_identifier" && (
                        <>
                            <SignatureAndIdentifier
                                key={page.type}
                                ref={signatureRef}
                                data={pageData}
                                company={context.company}
                                handleNext={onSign}
                                resign
                                isSubmitting={isSubmitting}
                            />
                            {submitError && (
                                <p className="text-sm text-red-600 -mt-6 mb-6 px-4 text-center">{submitError}</p>
                            )}
                        </>
                    )}

                    {pageIndex > 0 && (
                        <button
                            type="button"
                            onClick={() => { setPageIndex((index) => Math.max(index - 1, 0)); window.scrollTo(0, 0) }}
                            className="mb-10 inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-800"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back
                        </button>
                    )}
                </div>
            </div>
        )
    }

    const isEmailStep = step === STEP.EMAIL
    const linkUnavailable = !!linkStateError || (linkState && linkState.status !== "open")

    return (
        <div className="min-h-screen bg-gray-50">
            <PublicHeader referenceNumber={referenceNumber} />
            <div className="flex flex-col items-center justify-center px-4 py-10">
                <div className="w-full max-w-md">

                    <div className="flex flex-col items-center text-center mb-8">
                        <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl flex items-center justify-center mb-4">
                            <ShieldCheck className="w-6 h-6 text-white" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900">Re-Sign Your TruCheck™ Consent</h1>
                        {step !== STEP.DONE && !linkUnavailable && (
                            <p className="text-sm text-gray-600 mt-2">
                                {isEmailStep
                                    ? "Enter the email address this request was sent to. We will email you a one-time code."
                                    : "Enter the code we emailed you to open your consent."}
                            </p>
                        )}
                    </div>

                    {isLoadingState ? (
                        <p className="text-center text-sm text-gray-500">Loading...</p>
                    ) : step === STEP.DONE ? (
                        <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg p-6 text-center">
                            <div className="w-12 h-12 mx-auto rounded-full border flex items-center justify-center mb-4 text-green-600 bg-green-50 border-green-200">
                                <CheckCircle2 className="w-6 h-6" />
                            </div>
                            <h2 className="text-lg font-semibold text-gray-900">Thank you — your consent was signed</h2>
                            <p className="text-sm text-gray-600 mt-2">
                                Your re-signed consent has been recorded. No further action is needed for this step.
                            </p>
                        </div>
                    ) : linkUnavailable ? (
                        <StateCard status={linkStateError ? "not_found" : linkState.status} signedAt={linkState?.signedAt} />
                    ) : (
                        <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg p-6">
                            {submitError && isEmailStep && (
                                <p className="text-sm text-red-600 mb-4">{submitError}</p>
                            )}
                            {isEmailStep ? (
                                <form onSubmit={onSubmitEmail} className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Email address
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="you@example.com"
                                                autoComplete="email"
                                                required
                                                className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition"
                                            />
                                        </div>
                                    </div>

                                    {requestCodeError && (
                                        <p className="text-sm text-red-600">
                                            {requestCodeError?.response?.status === 403
                                                ? "That email doesn't match this request. Please use the email this request was sent to."
                                                : apiError(requestCodeError, "We couldn't send the code. Please try again.")}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isRequestingCode}
                                        className="w-full py-2.5 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
                                    >
                                        {isRequestingCode ? "Sending code..." : "Send code"}
                                    </button>
                                </form>
                            ) : (
                                <form onSubmit={onSubmitCode} className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            One-time code
                                        </label>
                                        <div className="relative">
                                            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                value={code}
                                                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                placeholder="Enter code"
                                                autoComplete="one-time-code"
                                                required
                                                className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition tracking-widest"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                            Sent to <span className="font-medium">{email}</span>
                                        </p>
                                    </div>

                                    {verifyCodeError && (
                                        <p className="text-sm text-red-600">
                                            {apiError(verifyCodeError, "That code is invalid or has expired. Please try again.")}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isVerifyingCode}
                                        className="w-full py-2.5 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
                                    >
                                        {isVerifyingCode ? "Verifying..." : "Open my consent"}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setStep(STEP.EMAIL)}
                                        className="w-full flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition"
                                    >
                                        <ArrowLeft className="w-3.5 h-3.5" />
                                        Use a different email / resend code
                                    </button>
                                </form>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ConsentResign
